import {
  Component,
  ElementRef,
  ViewEncapsulation,
  OnInit,
  AfterContentChecked,
  HostListener,
  ViewChild,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, zip } from 'rxjs'; // observable/zip
import * as _ from 'lodash';
import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import { environment } from '../../environments/environment';
import { FormatService } from '../core/query/format.service';
import { HeaderService } from './header.service';
import { TxAttributes } from '../models/transaction-attributes';
import { ProfileService } from '../core/user/profile.service';
import { ActiveFilterService } from '../core/query/active-filter.service';
import { ConfigService } from '../core/http/config.service';
import { ClientProfileService } from '../core/client/client-profile.service';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { LocaleFormat } from '../models';
import { getCurrencySymbol } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import * as Handlebars from '../../../node_modules/handlebars/dist/cjs/handlebars';
import { Heap } from '../core/interface/heap/heap-interface';
import { GridSortingService } from '../core/services/grid-sorting.service';
import { CookieConsentService } from '../core/services/cookie-consent.service';
import { FilterProfileService } from '../filter/profiles/filter-profile.service';
import { ClientBrandingService } from '@/app/core/client/client-branding.service';
import { CODENAMES } from '../core/constants';
import { ErrorService } from '../core/errors/error.service';
import { LocaleFormatsService } from '@/app/header/locale-formats.service';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';
import { AlertCountInfo } from './alert-menu.component';

declare let $: any;

@Component({
  selector: 'rdo-header',
  templateUrl: 'header.component.html',
  styleUrls: ['header.scss', 'header-search.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('ratesAndAvailabilitySelect')
  ratesAndAvailabilitySelect: ElementRef;
  selectedLocaleFilterDefault: any;
  isProduction = environment.production;
  isAlloyDBInUse: WritableSignal<boolean> = signal(false);
  version = '0.0.0.1-Angular';
  isUserPopupOpen = false;
  isAlertPopupOpen = false;
  isChangeLogPopupOpen = false;
  softwareVersion: string;
  searchCriteria: string;

  // Alert state (minimal — managed by AlertMenuComponent, exposed for bell icon)
  totalAlerts = signal(0);
  alertCount = 0;
  alertReadCount = 0;
  alertClientId: number = null;

  baseApiUrl: string;
  productTypeVisible = false;
  clientAttributes = new TxAttributes(<TxAttributes>{ IsTrial: false });
  hasAssetGrid = false;
  hasRatesAndAvailability = false;
  hasChangeLog = false;
  ratesAndAvailabilityUrl = environment.ratesAndAvailabilityUrl;
  showGuidedToursOptOut = environment.enableUnpromptedGuidance;
  showDisclaimer = false;

  private selectedCurrencySymbol: string;
  private subscriptions = [];
  private awaiting = {
    salesReps: false,
    customers: false,
    productTypes: false,
    assets: false,
  };
  public constructor(
    private formatService: FormatService,
    private searchService: HeaderService,
    private headerService: HeaderService,
    private localeFormatsService: LocaleFormatsService,
    private filterService: ActiveFilterService,
    private router: Router,
    private element: ElementRef,
    private profileService: ProfileService,
    private location: Location,
    private config: ConfigService,
    private clientProfileService: ClientProfileService,
    private hotKeysService: HotkeysService,
    public authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private gridSortingService: GridSortingService,
    private cookieConsentService: CookieConsentService,
    private filterProfileService: FilterProfileService,
    protected brandingService: ClientBrandingService,
    private errorService: ErrorService,
    public featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    this.isAlloyDBInUse.set(
      this.featureFlagService.isActive('flagRDOApiAlloyDBHeader')
    );
    if (
      this.authenticationService._userInfoView &&
      this.authenticationService._userInfoView.Id &&
      this.authenticationService._userInfoView.SelectedClient
    ) {
      this.hasRatesAndAvailability =
        this.authenticationService._userInfoView.SelectedClient.MQAAccess &&
        environment.ratesAndAvailabilityUrl.length > 0;
    }
    this.selectedLocaleFilterDefault =
      this.clientProfileService.selectedLocaleFilterDefault;
    this.subscriptions.push(
      this.clientProfileService.selectedLocaleSubject.subscribe(
        (filterValue) => {
          this.selectedLocaleFilterDefault = filterValue;
        }
      )
    );
    this.subscriptions.push(
      this.authenticationService.selectedClientId.subscribe((clientid) => {
        if (clientid) {
          this.softwareVersion = environment.softwareVersion;
          this.profileService.clear();
          this.config.baseApiUrl.subscribe((url) => {
            this.baseApiUrl = url;
          });
          if (
            this.authenticationService._userInfoView &&
            this.authenticationService._userInfoView.Id &&
            this.authenticationService._userInfoView.SelectedClient
          ) {
            this.hasRatesAndAvailability =
              this.authenticationService._userInfoView.SelectedClient
                .MQAAccess && environment.ratesAndAvailabilityUrl.length > 0;

            const hasRdoAccess =
              this.authenticationService._userInfoView.SelectedClient.RDOAccess;

            // Users that don't have RDO Access but do have downloads access should
            // not be downloading the localeFormats endpoint.
            const obs: Observable<
              | [TxAttributes, boolean]
              | [TxAttributes, boolean, Array<LocaleFormat>]
            > = hasRdoAccess
              ? zip(
                  this.clientProfileService.getClientAttributes(),
                  this.filterProfileService.waitForCurrentProfile(),
                  this.localeFormatsService.getLocaleFormats()
                )
              : zip(
                  this.clientProfileService.getClientAttributes(),
                  this.filterProfileService.waitForCurrentProfile()
                );

            obs.subscribe(
              (
                results:
                  | [TxAttributes, boolean]
                  | [TxAttributes, boolean, Array<LocaleFormat>]
              ) => {
                this.clientAttributes = results[0];

                const hasRdoAccess =
                  this.authenticationService._userInfoView.SelectedClient
                    .RDOAccess;

                // Downloads only users will not load locales so results[2] is undefined
                this.selectedCurrencySymbol = '';
                if (hasRdoAccess && results[2]) {
                  this.selectedCurrencySymbol = this.getCurrencySymbolFromSettings(results[2]);
                }

                this.hasAssetGrid =
                  !!this.filterProfileService.readClientProfileProperty(
                    CODENAMES.CN_HAS_ASSET_GRID
                  );
                this.hasChangeLog =
                  !!this.filterProfileService.readClientProfileProperty(
                    CODENAMES.CN_HAS_CHANGES_LOG
                  );

                if (this.clientAttributes) {
                  this.setupHotkeys();
                }
                this.setupSearch(this.hasAssetGrid);
              }
            );

            // Trigger alert loading in the AlertMenuComponent via input signal
            this.alertClientId =
              this.authenticationService._userInfoView.SelectedClient.ClientID;
          }
        }
      })
    );
  }

  ngOnDestroy() {
    while (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.pop().unsubscribe();
    }
  }

  // --- Alert count callback from AlertMenuComponent ---
  onAlertCountChanged(info: AlertCountInfo) {
    this.alertCount = info.alertCount;
    this.alertReadCount = info.alertReadCount;
    this.totalAlerts.set(info.totalAlerts);
  }

  /**
   * Calls a script from consent.trustarc.com declared
   * in the index.html file to show the cookies consent.
   */
  showCookiePreferences() {
    this.cookieConsentService.showModalAndBlackBar(true);
  }

  setShowCompetitionDisclaimer() {
    this.headerService.manuallyShowCompetitionDisclaimer = true;
  }

  showInApplicationGuidanceSettings() {
    this.headerService.showInApplicationGuidanceSettings = true;
  }

  openRatesAndAvailability(value: any) {
    if (value === 'ratesAndAvailability') {
      const params = `?cid=${this.authenticationService._userInfoView.SelectedClient.ClientID}`;
      window.open(`${this.ratesAndAvailabilityUrl}${params}`, '_blank');
      this.ratesAndAvailabilitySelect.nativeElement.value = 'rdo';
    }
  }

  showCompetitiveDisclaimerLink() {
    return environment.enableCompetitiveDisclaimer;
  }

  setupHotkeys = () => {
    if (!this.clientAttributes?.IsTrial) {
      this.hotKeysService.add(
        new Hotkey(
          'mod+s',
          (event: KeyboardEvent): boolean => {
            $('#searchbox').focus();
            return false;
          },
          null,
          this.translateService.instant(
            'main.navigation.search_bar.placeholder'
          )
        )
      );
    }

    this.hotKeysService.add(
      new Hotkey(
        'option+a',
        (event: KeyboardEvent): any => {
          // Check for Ctrl + Alt + A combination (only in non-production environments)
          if (!environment.production) {
            console.log('Alt+a or Option+a will open the admin panel');
            event.preventDefault(); // Prevent default browser behavior when using the shortcut
            this.router.navigate(['/admin']);
            return false;
          }
        },
        null
      )
    );

    if (this.hasChangeLog) {
      this.hotKeysService.add(
        new Hotkey(
          'mod+e',
          (event: KeyboardEvent): boolean => {
            if (!this.isChangeLogPopupOpen) {
              this.isChangeLogPopupOpen = true;
            }
            $('#changeLogComment').focus();
            return false;
          },
          null,
          this.translateService.instant(
            'main.navigation.changes_log_entry.title'
          )
        )
      );
    }
  };

  setSearchFocus = () => {
    $('#searchbox').focus();
  };

  toggleUserPopup = () => {
    this.isUserPopupOpen = !this.isUserPopupOpen;
    if (
      this.isUserPopupOpen &&
      (this.isAlertPopupOpen || this.isChangeLogPopupOpen)
    ) {
      this.isAlertPopupOpen = this.isChangeLogPopupOpen = false;
    }
  };

  toggleAlertPopup = () => {
    this.isAlertPopupOpen = !this.isAlertPopupOpen;
    if (
      this.isAlertPopupOpen &&
      (this.isUserPopupOpen || this.isChangeLogPopupOpen)
    ) {
      this.isUserPopupOpen = this.isChangeLogPopupOpen = false;
    }
  };

  toggleChangeLogPopup = () => {
    this.isChangeLogPopupOpen = !this.isChangeLogPopupOpen;
    if (
      this.isChangeLogPopupOpen &&
      (this.isUserPopupOpen || this.isAlertPopupOpen)
    ) {
      this.isUserPopupOpen = this.isAlertPopupOpen = false;
    }
    if (this.isChangeLogPopupOpen) {
      $('#changeLogComment').focus();
    }
  };

  toggleMulticlientPopup = (ishidebackground: boolean) => {
    this.authenticationService
      .promptClientSelection()
      .subscribe((selectedclient) => {
        if (selectedclient) {
          this.authenticationService._userInfoView.updateSelectedClient(
            selectedclient
          );
          this.authenticationService.selectedClientId.next(
            selectedclient.ClientID
          );
        }
      });
  };

  windowPopup = () => {
    const newUrl = '/assets/images/US_Canada_Maps.pdf';
    window.open(
      newUrl,
      'popup',
      'width=800,height=600,location=no,toolbar=no,menubar=no,scrollbars=yes,resizable=yes'
    );
  };

  windowGuidePopup = () => {
    const newUrl = '/assets/images/RDO_User_Guide.pdf';
    window.open(
      newUrl,
      'popup',
      'width=800,height=600,location=no,toolbar=no,menubar=no,scrollbars=yes,resizable=yes'
    );
  };

  setupSearch = (hasAssetGrid: boolean) => {
    // eslint-disable-next-line
    // @ts-ignore
    const options: Twitter.Typeahead.Options = {
      minLength: 1,
      hint: false,
      highlight: true,
    };
    const itemTemplate = `<div data-type="suggestion-link">
            <div class="searchbox-result-column text-uppercase" title="{{Title}}">{{Title}}</div>
            <div class="searchbox-benchmark-column">{{ClientBenchmarkedRevenueDisplay}}</div>
            <div class="searchbox-difference-column searchbox-diff-below-{{IsBenchmarkedDifferenceBelow}}">{{BenchmarkedRevenueDifferenceDisplay}}</div>
            </div>`;
    const rentalAssetItemTemplate = `<div data-type="suggestion-link">
            <div class="searchbox-result-column text-uppercase" title="{{Title}}">{{Title}}</div>
            <div class="searchbox-benchmark-column">-</div>
            <div class="searchbox-difference-column searchbox-diff-below-false">-</div>
            </div>`;
    const getHeader = () => {
      return `<div class="searchbox-results-header">
                ${this.translateService.instant('main.navigation.search_bar.filtered_dearch_results')}:
            </div>
            <div class="searchbox-headers float-right">
                ${this.formatService.translateAndFormat('main.navigation.search_bar.compared_revenue, ' + this.selectedCurrencySymbol)}
            </div><div class="searchbox-headers float-right">
                ${this.formatService.translateAndFormat('main.navigation.search_bar.difference, ' + this.selectedCurrencySymbol)}
            </div>`;
    };
    // eslint-disable-next-line
    // @ts-ignore
    const dataset: Twitter.Typeahead.Dataset<any>[] = [
      {
        display: (_) => '',
        source: (q, sync, async) => {
          this.awaiting.salesReps = true;
          this.searchService.searchSalesReps(q).subscribe(
            (r) => {
              this.awaiting.salesReps = false;
              async(r);
            },
            (err) => {
              this.awaiting.salesReps = false;
              const message = err
                ? err.message
                  ? err.message
                  : err.toString()
                : 'Error is null';
              this.errorService.logSentryError(
                err,
                `header.service:search failed: ${message}`
              );
              async([]);
            }
          );
        },
        limit: 10,
        templates: {
          header: () => {
            return `${getHeader()}
                        <h5 class="search-header"><i class="fa fa-fw fa-user"></i>
                            ${this.translateService.instant('main.navigation.search_bar.sales_reps')}
                        </h5>`;
          },
          notFound: () => {
            return `${getHeader()}
                        <h5 class="search-header"><i class="fa fa-fw fa-user"></i>
                            ${this.translateService.instant('main.navigation.search_bar.sales_reps')}
                        </h5>&emsp;&emsp;<em>
                            ${this.translateService.instant('main.navigation.search_bar.no_matching_sales_reps')}
                        </em>`;
          },
          suggestion: Handlebars.compile(itemTemplate),
        },
      },
      {
        display: (_) => '',
        source: (q, sync, async) => {
          this.awaiting.customers = true;
          this.searchService.searchCustomers(q).subscribe(
            (r) => {
              this.awaiting.customers = false;
              async(r);
            },
            (err) => {
              this.awaiting.customers = false;
              async([]);
            }
          );
        },
        limit: 10,
        templates: {
          header: () => {
            return `<h5 class="search-header"><i class="fa fa-fw fa-building"></i>
                        ${this.translateService.instant('main.navigation.search_bar.customers')}</h5>`;
          },
          notFound: () => {
            return `<h5 class="search-header"><i class="fa fa-fw fa-building"></i>
                        ${this.translateService.instant('main.navigation.search_bar.customers')}
                        </h5>&emsp;&emsp;<em>
                        ${this.translateService.instant('main.navigation.search_bar.no_matching_customers')}</em>`;
          },
          suggestion: Handlebars.compile(itemTemplate),
        },
      },
      {
        display: (_) => '',
        source: (q, sync, async) => {
          this.awaiting.productTypes = true;
          this.searchService.searchProductTypes(q).subscribe(
            (r) => {
              this.awaiting.productTypes = false;
              async(r);
            },
            (err) => {
              this.awaiting.productTypes = false;
              async([]);
            }
          );
        },
        limit: 10,
        templates: {
          header: () => {
            return `<h5 class="search-header"><img src="/assets/images/icons/gear.svg" /><span>
                        ${this.translateService.instant('main.navigation.search_bar.equipment')}</span></h5>`;
          },
          notFound: () => {
            return `<h5 class="search-header"><img src="/assets/images/icons/gear.svg" /><span>
                        ${this.translateService.instant('main.navigation.search_bar.equipment')}</span></h5>&emsp;&emsp;<em>
                        ${this.translateService.instant('main.navigation.search_bar.no_matching_equipment')}</em>`;
          },
          suggestion: Handlebars.compile(itemTemplate),
        },
      },
    ];
    if (hasAssetGrid) {
      // eslint-disable-next-line
      // @ts-ignore
      dataset.push(<Twitter.Typeahead.Dataset<any>>{
        display: (_) => '',
        source: (q, sync, async) => {
          this.awaiting.assets = true;
          this.searchService.searchAssets(q).subscribe(
            (r) => {
              this.awaiting.assets = false;
              async(r);
            },
            (err) => {
              this.awaiting.assets = false;
              async([]);
            }
          );
        },
        limit: 10,
        templates: {
          header: () => {
            return `<h5 class="search-header"><i class="fa fa-fw fa-tags"></i><span>&nbsp;&nbsp;
                        ${this.translateService.instant('main.navigation.search_bar.assets')}</span></h5>`;
          },
          notFound: () => {
            return `<h5 class="search-header"><i class="fa fa-fw fa-tags"></i><span>
                        ${this.translateService.instant('main.navigation.search_bar.assets')}
                        </span></h5>&emsp;&emsp;<em>
                        ${this.translateService.instant('main.navigation.search_bar.no_matching_assets')}</em>`;
          },
          suggestion: Handlebars.compile(rentalAssetItemTemplate),
        },
      });
    }

    $('.typeahead')
      .typeahead(options, dataset)
      .on('typeahead:asyncrequest', () => {
        $('#search-spinner').show();
      })
      .on('typeahead:asynccancel typeahead:asyncreceive', () => {
        if (
          !this.awaiting.salesReps &&
          !this.awaiting.customers &&
          !this.awaiting.productTypes &&
          !this.awaiting.assets
        ) {
          $('#search-spinner').hide();
        }
      })
      .on('typeahead:selected', (event, suggestion) => {
        this.searchCriteria = null;
        switch (suggestion.Type) {
          case 'product-type':
            // eslint-disable-next-line no-case-declarations
            const useRentedAsProductType =
              this.filterService.getCurrentFilter().useRentedAsProductTypes ===
              1;
            // eslint-disable-next-line no-case-declarations
            const treeConfig = [
              '/equipment',
              'product-types',
              suggestion.SurrogateId,
              {
                category: '',
                reloadCards: 'true',
                useRentedAsProductType,
              },
            ];

            if (useRentedAsProductType) {
              treeConfig.push('rate-types');
            }
            // eslint-disable-next-line no-case-declarations
            const urlTree = this.router.createUrlTree(treeConfig);

            this.router.navigateByUrl(urlTree);
            break;
          case 'customer':
            this.router.navigate([
              '/customers',
              suggestion.SurrogateId,
              { reloadCards: 'true' },
            ]);
            break;
          case 'sales-rep':
            this.router.navigate([
              '/sales-reps',
              suggestion.SurrogateId,
              { reloadCards: 'true' },
            ]);
            break;
          case 'asset':
            this.router.navigate([
              '/rental-assets',
              suggestion.SurrogateId,
              { reloadCards: 'true' },
            ]);
            break;
          default:
            console.log(
              this.translateService.instant(
                'main.navigation.search_bar.search_item_not_supported'
              )
            );
        }
        $('#searchbox').blur();
      });
  };

  public signOut = () => {
    this.gridSortingService.clearSortStorage();
    ((window as any).heap as Heap)?.resetIdentity();
    this.authenticationService.logout();
  };

  private getCurrencySymbolFromSettings = (
    localeFormats: Array<LocaleFormat>
  ): string => {
    let result: string = '';
    const currencydefault = this.selectedLocaleFilterDefault
      ? _.find(
          localeFormats,
          (lf) => lf.LocaleFormatID === this.selectedLocaleFilterDefault
        )
      : null;
    result = currencydefault
      ? getCurrencySymbol(currencydefault.CurrencyCode, 'narrow')
      : '';
    return result;
  };

  navigateToAdmin() {
    this.router.navigate(['/admin']);
  }

  @HostListener('document:click', ['$event'])
  private closeOpenedMenu = (e: any) => {
    let isParent = false;
    const child = e.target;
    if (this.isUserPopupOpen) {
      const parent = this.element.nativeElement.querySelector('.avatar');
      let node = child.parentNode;
      while (node !== null) {
        if (node === parent) {
          isParent = true;
        }
        node = node.parentNode;
      }
      if (!isParent) {
        this.isUserPopupOpen = false;
      }
    }
    if (this.isAlertPopupOpen) {
      const parent = this.element.nativeElement.querySelector('.alerts');
      if (child === parent) {
        return;
      }
      const closestParent =
        this.element.nativeElement.querySelector('#alert-menu');
      let node = child.parentNode;
      while (node !== null) {
        if (node === parent || node === closestParent) {
          isParent = true;
        }
        node = node.parentNode;
      }
      if (!isParent) {
        this.isAlertPopupOpen = false;
      }
    }
  };

  private resetTypeahead() {
    $('.typeahead').typeahead('val', '');
  }

  public reinitializeHeader() {
    // TODO: This is in the template but was not defined.
  }
}
